import { Button } from "@/components/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@primitives/dropdown-menu";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@primitives/drawer";
import { ChatUserAction, useNavigationState } from "@/zustand/NavigationState";
import {
  Plus,
  MessageCircleIcon,
  UsersRoundIcon,
  NotebookPen,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { IS_MOBILE } from "@/lib/environmentDetector";
import { prepareGroupChatOwner } from "@/middleware/members";
import { useToast } from "@/hooks/useToast";
import { useNewGroupChatState } from "@/zustand/NewGroupChatState";

export default function NewChatMenu() {
  const setChatUserAction = useNavigationState(
    (state) => state.setChatUserAction,
  );
  const initNewChat = useNewGroupChatState((state) => state.initNewChat);
  const resetNewGroupChatState = useNewGroupChatState(
    (state) => state.resetNewGroupChatState,
  );

  const { toast } = useToast();
  const { t } = useTranslation();

  const handleGroupChat = async () => {
    try {
      // genrate a unique id and csk
      initNewChat();
      // prepare the owner of the group chat -> secure payload
      await prepareGroupChatOwner();
      setChatUserAction(ChatUserAction.CREATE_GROUP_CHAT);
    } catch (error) {
      resetNewGroupChatState();
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  if (IS_MOBILE) {
    return (
      <Drawer>
        <DrawerTrigger className="ml-auto">
          <Button
            asChild
            className="size-5 cursor-pointer"
            size="icon"
            variant="ghost"
          >
            <Plus strokeWidth={2} />
          </Button>
        </DrawerTrigger>
        <DrawerContent aria-describedby="contact-menu" className="px-4 pb-4">
          <DrawerHeader />
          <DrawerTitle />
          <DrawerDescription />

          <DrawerClose asChild>
            <Button
              variant="link"
              className="mb-1 flex items-center justify-start gap-3 text-base text-foreground no-underline hover:no-underline"
              onClick={() => setChatUserAction(ChatUserAction.CREATE_P2P_CHAT)}
            >
              <MessageCircleIcon strokeWidth={2} className="size-4" />
              {t("newMessage")}
            </Button>
          </DrawerClose>

          <DrawerClose asChild>
            <Button
              variant="link"
              className="mb-1 flex items-center justify-start gap-3 text-base text-foreground no-underline hover:no-underline"
              onClick={() => handleGroupChat()}
            >
              <UsersRoundIcon className="size-4" /> {t("newGroup")}
            </Button>
          </DrawerClose>

          <DrawerClose asChild>
            <Button
              disabled
              variant="link"
              className="mb-1 flex items-center justify-start gap-3 text-base text-foreground no-underline hover:no-underline"
            >
              <NotebookPen className="size-4" /> {t("noteToSelf")}
            </Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="ml-auto flex items-center">
        <Button
          asChild
          className="size-5 cursor-pointer"
          size="icon"
          variant="ghost"
        >
          <Plus strokeWidth={2} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="flex flex-col gap-0">
        <DropdownMenuItem
          onClick={() => setChatUserAction(ChatUserAction.CREATE_P2P_CHAT)}
          className="flex items-center lg:cursor-pointer"
        >
          <MessageCircleIcon className="size-4" /> {t("newMessage")}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => handleGroupChat()}
          className="flex items-center lg:cursor-pointer"
        >
          <UsersRoundIcon className="size-4" />
          {t("newGroup")}
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled
          className="flex items-center lg:cursor-pointer"
        >
          <NotebookPen className="size-4" />
          {t("noteToSelf")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
